import React from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import WifiOffOutlinedIcon from '@mui/icons-material/WifiOffOutlined';
import {
  OFFLINE_USER_MESSAGE,
  OFFLINE_USER_TITLE,
} from '../utils/errorUtils';

export type NetworkErrorStateProps = {
  message?: string;
  title?: string;
  onRetry?: () => void;
  retryDisabled?: boolean;
  retrying?: boolean;
  reconnecting?: boolean;
  minHeight?: number | string;
};

/**
 * Unified offline / network-failure empty state (minimal, app-consistent).
 */
export const NetworkErrorState: React.FC<NetworkErrorStateProps> = ({
  message = OFFLINE_USER_MESSAGE,
  title = OFFLINE_USER_TITLE,
  onRetry,
  retryDisabled = false,
  retrying = false,
  reconnecting = false,
  minHeight = 200,
}) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight,
        height: '100%',
        px: 2,
        py: 4,
      }}
    >
      <Paper
        elevation={0}
        sx={{
          width: '100%',
          maxWidth: 400,
          px: 3,
          py: 4,
          textAlign: 'center',
          borderRadius: 2,
          border: `1px solid ${theme.palette.divider}`,
          bgcolor: theme.palette.background.paper,
        }}
      >
        <Box
          sx={{
            width: 56,
            height: 56,
            mx: 'auto',
            mb: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '50%',
            bgcolor: alpha(theme.palette.text.primary, 0.06),
          }}
        >
          <WifiOffOutlinedIcon sx={{ fontSize: 28, color: 'text.secondary' }} />
        </Box>

        <Typography variant="h6" fontWeight={600} gutterBottom>
          {reconnecting ? 'Conexão restabelecida' : title}
        </Typography>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 3, lineHeight: 1.6 }}>
          {reconnecting ? 'Carregando a página…' : message}
        </Typography>

        {onRetry ? (
          <Button
            variant="outlined"
            size="medium"
            startIcon={
              retrying || reconnecting ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <RefreshIcon />
              )
            }
            onClick={onRetry}
            disabled={retryDisabled || retrying || reconnecting}
            sx={{ minWidth: 160 }}
          >
            {retrying || reconnecting ? 'Carregando…' : 'Tentar novamente'}
          </Button>
        ) : null}

        {retryDisabled && !reconnecting ? (
          <Typography variant="caption" color="text.disabled" display="block" sx={{ mt: 2 }}>
            Aguardando conexão… a página recarrega automaticamente ao voltar online.
          </Typography>
        ) : null}
      </Paper>
    </Box>
  );
};
